<template>
  <v-dialog :value="value" max-width="720" persistent @input="$emit('input', $event)">
    <v-card class="blog-composer" dark>
      <v-card-title class="blog-composer__title">
        <span>{{ post ? "Editar matéria" : "Nova matéria" }}</span>
        <v-btn icon aria-label="Fechar" :disabled="submitting" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="blog-composer__body">
        <template v-if="categoriesLoading">
          <div class="blog-composer__loading">
            <v-progress-circular indeterminate color="#ffcf5a" size="28" />
            <span>Carregando categorias…</span>
          </div>
        </template>

        <template v-else-if="categoriesError">
          <div class="blog-composer__error" role="alert">
            <p>Não foi possível carregar as categorias.</p>
            <v-btn small outlined color="#ffcf5a" :disabled="submitting" @click="loadCategories">Tentar novamente</v-btn>
          </div>
        </template>

        <template v-else>
          <v-text-field
            v-model="title"
            outlined
            dense
            counter="180"
            maxlength="180"
            label="Título da matéria"
            hint="O título é usado na capa e no endereço da matéria."
            :error-messages="fieldErrors('title')"
            :disabled="submitting"
          />

          <v-textarea
            v-model="content"
            outlined
            auto-grow
            rows="6"
            counter="20000"
            maxlength="20000"
            label="Conteúdo"
            :error-messages="fieldErrors('content')"
            :disabled="submitting"
          />

          <v-select
            v-model="categoryId"
            :items="categoryItems"
            item-text="name"
            item-value="id"
            outlined
            dense
            label="Categoria"
            placeholder="Escolha a categoria da matéria"
            :error-messages="fieldErrors('social_post_category_id')"
            :disabled="submitting || !categoryItems.length"
            no-data-text="Nenhuma categoria disponível"
          />

          <p class="blog-composer__cover-note">
            A primeira imagem é usada como capa da matéria.
          </p>

          <div v-if="previews.length" class="blog-composer__previews">
            <figure v-for="(preview, index) in previews" :key="preview.key" class="blog-composer__preview">
              <img :src="preview.url" :alt="`Imagem ${index + 1} da matéria`" />
              <span v-if="index === 0" class="blog-composer__cover-tag">Capa</span>
              <div class="blog-composer__preview-actions">
                <v-btn fab x-small text aria-label="Mover imagem para a frente" :disabled="submitting || index === 0" @click="movePreview(index, -1)">
                  <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <v-btn fab x-small text aria-label="Mover imagem para trás" :disabled="submitting || index === previews.length - 1" @click="movePreview(index, 1)">
                  <v-icon>mdi-arrow-right</v-icon>
                </v-btn>
                <v-btn fab x-small text aria-label="Remover imagem" :disabled="submitting" @click="removePreview(index)">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </figure>
          </div>

          <input
            id="blog-post-images"
            ref="fileInput"
            class="sr-only"
            name="blog_post_images"
            aria-label="Selecionar imagens da matéria"
            type="file"
            accept="image/*"
            multiple
            @change="selectImages"
          />
          <div class="blog-composer__footer">
            <v-btn text :disabled="submitting || previews.length >= 4" @click="$refs.fileInput.click()">
              <v-icon left color="#ffcf5a">mdi-image-multiple-outline</v-icon> Imagens
            </v-btn>
            <small>{{ previews.length }}/4</small>
          </div>

          <v-alert v-if="localError" dense text type="error" class="mt-3">{{ localError }}</v-alert>
        </template>
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <v-spacer />
        <v-btn text :disabled="submitting" @click="close">Cancelar</v-btn>
        <v-btn
          class="zinnes-primary-btn"
          depressed
          :loading="submitting"
          :disabled="submitting || categoriesLoading || !!categoriesError"
          @click="submit"
        >
          {{ post ? "Salvar matéria" : "Publicar matéria" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { socialMediaUrl } from "@/server/SocialService";
import { getBlogCategories } from "@/server/BlogService";

const MAX_BYTES = 6 * 1024 * 1024;

export default {
  name: "BlogComposer",
  props: {
    value: { type: Boolean, default: false },
    post: { type: Object, default: null },
    submitting: { type: Boolean, default: false },
    error: { type: String, default: "" },
  },
  data: () => ({
    title: "",
    content: "",
    categoryId: null,
    keptImages: [],
    newImages: [],
    objectUrls: [],
    categories: [],
    categoriesLoading: false,
    categoriesError: false,
    localError: "",
  }),
  computed: {
    categoryItems() {
      return this.categories;
    },
    previews() {
      return [
        ...this.keptImages.map((image) => ({ key: `kept-${image.id}`, url: socialMediaUrl(image.url), id: image.id })),
        ...this.newImages.map((file, index) => ({ key: `new-${file.name}-${index}`, url: this.objectUrls[index], file })),
      ];
    },
    canSubmit() {
      return (
        !this.submitting
        && this.title.trim().length >= 10
        && this.title.trim().length <= 180
        && this.content.trim().length >= 1
        && this.previews.length >= 1
        && this.categoryId != null
        && this.categories.some((category) => category.id === this.categoryId)
      );
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
    post() {
      if (this.value) this.reset();
    },
  },
  beforeDestroy() {
    this.revokeUrls();
  },
  methods: {
    reset() {
      this.revokeUrls();
      this.title = this.post?.title || "";
      this.content = this.post?.content || "";
      this.categoryId = this.post?.category ? this.post.category.id : null;
      this.keptImages = [...(this.post?.images || [])];
      this.newImages = [];
      this.objectUrls = [];
      this.localError = "";
      this.loadCategories();
    },
    async loadCategories() {
      this.categoriesLoading = true;
      this.categoriesError = false;
      try {
        const body = await getBlogCategories();
        this.categories = body.data?.items || [];
      } catch {
        this.categoriesError = true;
      } finally {
        this.categoriesLoading = false;
      }
    },
    revokeUrls() {
      this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    },
    close() {
      if (!this.submitting) this.$emit("input", false);
    },
    fieldErrors(field) {
      const local = this.localError && this.errorMatch(field) ? [this.localError] : [];
      if (this.error && this.errorMatch(field)) return [this.error];
      return local;
    },
    errorMatch(field) {
      if (field === "title") return /t[íi]tulo/i.test(this.error);
      if (field === "content") return /conte[úu]do/i.test(this.error);
      if (field === "social_post_category_id") return /categoria/i.test(this.error);
      return false;
    },
    selectImages(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      this.localError = "";
      if (files.some((file) => file.size > MAX_BYTES)) {
        this.localError = "Cada imagem pode ter no máximo 6 MB.";
        return;
      }
      if (files.some((file) => !file.type.startsWith("image/") || file.type === "image/svg+xml")) {
        this.localError = "Selecione apenas imagens raster válidas.";
        return;
      }
      const available = 4 - this.previews.length;
      if (files.length > available) {
        this.localError = "Uma matéria pode ter no máximo quatro imagens.";
        return;
      }
      files.forEach((file) => {
        this.newImages.push(file);
        this.objectUrls.push(URL.createObjectURL(file));
      });
    },
    movePreview(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.previews.length) return;
      if (index < this.keptImages.length) {
        this.swap(this.keptImages, index, target);
      } else {
        const newIndex = index - this.keptImages.length;
        const newTarget = target - this.keptImages.length;
        if (newIndex >= 0 && newTarget >= 0 && newIndex < this.newImages.length && newTarget < this.newImages.length) {
          this.swap(this.newImages, newIndex, newTarget);
          this.swap(this.objectUrls, newIndex, newTarget);
        }
      }
    },
    swap(array, from, to) {
      const value = array[from];
      this.$set(array, from, array[to]);
      this.$set(array, to, value);
    },
    removePreview(index) {
      if (index < this.keptImages.length) {
        this.keptImages.splice(index, 1);
      } else {
        const newIndex = index - this.keptImages.length;
        URL.revokeObjectURL(this.objectUrls[newIndex]);
        this.newImages.splice(newIndex, 1);
        this.objectUrls.splice(newIndex, 1);
      }
    },
    submit() {
      const trimmedTitle = this.title.trim();
      const trimmedContent = this.content.trim();
      this.localError = "";

      if (trimmedTitle.length < 10) {
        this.localError = "O título precisa ter pelo menos 10 caracteres.";
        this.focusField("title");
        return;
      }
      if (trimmedTitle.length > 180) {
        this.localError = "O título não pode ultrapassar 180 caracteres.";
        this.focusField("title");
        return;
      }
      if (!trimmedContent) {
        this.localError = "O conteúdo é obrigatório.";
        this.focusField("content");
        return;
      }
      if (!this.previews.length) {
        this.localError = "A matéria precisa ter pelo menos uma imagem.";
        this.focusField("images");
        return;
      }
      if (this.categoryId == null || !this.categories.some((category) => category.id === this.categoryId)) {
        this.localError = "Escolha a categoria da matéria antes de publicar.";
        this.focusField("category");
        return;
      }

      this.$emit("submit", {
        title: trimmedTitle,
        content: trimmedContent,
        categoryId: this.categoryId,
        images: this.newImages,
        keptImageIds: this.keptImages.map((image) => image.id),
      });
    },
    focusField(field) {
      this.$nextTick(() => {
        const selectors = {
          title: ".blog-composer input[type='text']",
          content: ".blog-composer textarea",
          category: ".blog-composer .v-select",
          images: "#blog-post-images",
        };
        const target = document.querySelector(selectors[field]);
        if (!target) return;
        target.focus();
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },
  },
};
</script>